import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { fetchCurrentUser } from '../api/ideas';
import SettingsModal from './SettingsModal';
import ThemeMenu from './ThemeMenu';
import { FaSun, FaMoon, FaCog, FaSignOutAlt, FaSignInAlt, FaRegSun, FaLightbulb, FaRegLightbulb, FaPalette } from 'react-icons/fa';

function Navbar({ token, setToken, openLoginModal, refreshKey }) {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [user, setUser] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const themeButtonRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setUser(null);
    }
  }, [token, refreshKey]);

  const fetchUser = async () => {
    try {
      const response = await fetchCurrentUser(token);
      setUser(response);
      setError('');
    } catch (err) {
      setError('Failed to fetch user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('is_admin');
    setUser(null);
    navigate('/');
  };

  const handleThemeButtonHover = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setShowThemeMenu(true);
    }, 500); // Show menu after 500ms hover
  };

  const handleThemeButtonLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Don't close immediately, let ThemeMenu handle its own hover
  };

  const handleThemeButtonRightClick = (e) => {
    e.preventDefault();
    setShowThemeMenu(!showThemeMenu);
  };

  const handleThemeButtonClick = () => {
    // Toggle theme menu on left click
    setShowThemeMenu(!showThemeMenu);
    //toggleTheme();
  };

  return (
    <nav className="bg-[var(--bg-secondary)] p-4 shadow-lg flex justify-between items-center">
      {/* Added left padding for mobile hamburger menu space */}
      <h1 className="text-xl font-bold text-[var(--text-primary)] pl-10 lg:pl-0 flex items-center gap-2">
        <FaLightbulb className="text-[var(--primary)]" />
        Idea Hub
      </h1>
      <div className="flex items-center space-x-2 sm:space-x-4">
        {token && user ? (
          <>
            {/* Only show username on larger screens */}
            <span className="hidden sm:inline text-[var(--text-primary)] text-sm">
              {user.display_name || user.username}
            </span>
            <button
              onClick={() => setShowSettingsModal(true)}
              className="p-2 rounded-lg text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-all duration-200 hover:scale-110 active:scale-95 hover:shadow-md"
              aria-label="Settings"
            >
              <FaCog className="transition-transform duration-200 hover:rotate-45" />
            </button>
            <button
              ref={themeButtonRef}
              onClick={handleThemeButtonClick}
              onContextMenu={handleThemeButtonRightClick}
              onMouseEnter={handleThemeButtonHover}
              onMouseLeave={handleThemeButtonLeave}
              className="p-2 rounded-lg text-[var(--text-primary)] hover:bg-[var(--bg-hover)] relative transition-all duration-200 hover:scale-110 active:scale-95 hover:shadow-md"
              aria-label="Toggle theme or right-click for theme menu"
            >
              <FaPalette className="transition-transform duration-300 hover:scale-110" />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 sm:px-3 sm:py-1 bg-[var(--primary)] text-white rounded-lg text-sm hover:opacity-90 flex items-center transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-lg transform"
              aria-label="Logout"
            >
              <span className="hidden sm:inline">Logout</span>
              <FaSignOutAlt className="sm:hidden transition-transform duration-200 hover:translate-x-1" />
            </button>
          </>
        ) : (
          <>
            <button
              ref={themeButtonRef}
              onClick={handleThemeButtonClick}
              onContextMenu={handleThemeButtonRightClick}
              onMouseEnter={handleThemeButtonHover}
              onMouseLeave={handleThemeButtonLeave}
              className="p-2 rounded-lg text-[var(--text-primary)] hover:bg-[var(--bg-hover)] relative transition-all duration-200 hover:scale-110 active:scale-95 hover:shadow-md"
              aria-label="Toggle theme or right-click for theme menu"
            >
              <FaPalette className="transition-transform duration-300 hover:scale-110" />
            </button>
            <button
              onClick={openLoginModal}
              className="p-2 sm:px-3 sm:py-1 bg-[var(--primary)] text-white rounded-lg text-sm hover:opacity-90 flex items-center transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-lg transform"
              aria-label="Login"
            >
              <span className="hidden sm:inline">Login</span>
              <FaSignInAlt className="sm:hidden transition-transform duration-200 hover:translate-x-1" />
            </button>
          </>
        )}
      </div>
      {showSettingsModal && (
        <SettingsModal
          closeModal={() => setShowSettingsModal(false)}
          user={user}
          token={token}
        />
      )}
      <ThemeMenu 
        isVisible={showThemeMenu}
        onClose={() => setShowThemeMenu(false)}
        buttonRef={themeButtonRef}
      />
    </nav>
  );
}

export default Navbar;